/*
 * The docs shell is shared with the endorsement documentation rather than
 * copied, so the two products read as one manual. Cargo adds no styles of its
 * own; anything it needs belongs in that file.
 */
import '../ListZeroWorkflow/list-zero-workflow.scss';

/*
 * How a cargo claim travels between AZADEA and Clover.
 *
 * Written for the people who work the claims rather than for the integration:
 * the flow, what moves it, and the two rules that surprise people. The field
 * level detail lives on the fields page.
 */

const COLORS = {
  decision: '#1d4ed8',
  decisionBg: '#eff6ff',
  outcome: '#475569',
  outcomeBg: '#f8fafc',
  good: '#2d8a4e',
  bad: '#d9534f',
  waiting: '#f0ad4e',
  line: '#64748b',
};

type Variant = 'start' | 'step' | 'good' | 'bad' | 'waiting';

const VARIANTS: Record<Variant, { fill: string; stroke: string; text: string }> = {
  start: { fill: '#0f172a', stroke: '#0f172a', text: '#ffffff' },
  step: { fill: COLORS.outcomeBg, stroke: COLORS.outcome, text: '#334155' },
  good: { fill: COLORS.good, stroke: COLORS.good, text: '#ffffff' },
  bad: { fill: COLORS.bad, stroke: COLORS.bad, text: '#ffffff' },
  waiting: { fill: COLORS.waiting, stroke: COLORS.waiting, text: '#ffffff' },
};

type NodeProps = {
  x: number;
  y: number;
  w: number;
  h: number;
  lines: string[];
  variant?: Variant;
};

function Node({ x, y, w, h, lines, variant = 'step' }: NodeProps) {
  const s = VARIANTS[variant];
  const lineHeight = 15;
  const startY = y + h / 2 - ((lines.length - 1) * lineHeight) / 2;

  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={8} fill={s.fill} stroke={s.stroke} strokeWidth={1.5} />
      {lines.map((line, i) => (
        <text
          key={i}
          x={x + w / 2}
          y={startY + i * lineHeight}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={13}
          fontWeight={500}
          fill={s.text}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

type ArrowProps = { points: [number, number][]; label?: string; dashed?: boolean };

function Arrow({ points, label, dashed }: ArrowProps) {
  const d = points.map(([px, py], i) => `${i === 0 ? 'M' : 'L'} ${px} ${py}`).join(' ');
  const [lx, ly] = points[0];

  return (
    <g>
      <path
        d={d}
        fill="none"
        stroke={COLORS.line}
        strokeWidth={1.5}
        strokeDasharray={dashed ? '5 4' : undefined}
        markerEnd="url(#cargo-arrowhead)"
      />
      {label && (
        <text x={lx + 8} y={ly - 7} fontSize={12} fontWeight={600} fill={COLORS.line}>
          {label}
        </text>
      )}
    </g>
  );
}

function LifecycleDiagram() {
  return (
    <svg viewBox="0 0 960 330" className="workflow-diagram" role="img" aria-label="How a cargo claim moves from notification to closure">
      <defs>
        <marker id="cargo-arrowhead" markerWidth="9" markerHeight="7" refX="8" refY="3.5" orient="auto">
          <polygon points="0 0, 9 3.5, 0 7" fill={COLORS.line} />
        </marker>
      </defs>

      <Node x={12} y={72} w={124} h={52} lines={['AZADEA', 'notifies a claim']} variant="start" />
      <Node x={196} y={72} w={116} h={52} lines={['Received']} />
      <Node x={372} y={72} w={116} h={52} lines={['Documented']} />
      <Node x={548} y={72} w={116} h={52} lines={['In review']} />

      <Node x={724} y={12} w={112} h={44} lines={['Accepted']} variant="good" />
      <Node x={724} y={76} w={112} h={44} lines={['Unsettled']} variant="waiting" />
      <Node x={724} y={140} w={112} h={44} lines={['Rejected']} variant="bad" />
      <Node x={860} y={12} w={88} h={44} lines={['Closed']} variant="good" />

      <Node x={548} y={258} w={116} h={44} lines={['Cancelled']} variant="bad" />

      <Arrow points={[[136, 98], [192, 98]]} />
      <Arrow points={[[312, 98], [368, 98]]} label="details + 6 documents" />
      <Arrow points={[[488, 98], [544, 98]]} label="Clover picks it up" />

      <Arrow points={[[664, 92], [694, 92], [694, 34], [720, 34]]} label="decision" />
      <Arrow points={[[664, 98], [720, 98]]} />
      <Arrow points={[[664, 104], [694, 104], [694, 162], [720, 162]]} />

      <Arrow points={[[780, 76], [780, 60]]} />
      <Arrow points={[[836, 34], [856, 34]]} label="credit note" />

      <Arrow points={[[254, 124], [254, 280], [544, 280]]} dashed />
      <Arrow points={[[430, 124], [430, 280]]} dashed />
      <Arrow points={[[606, 124], [606, 254]]} dashed />

      <text x={276} y={302} fontSize={12} fill={COLORS.line}>
        either side can cancel, and a claim with no documents after 90 days is cancelled automatically
      </text>
    </svg>
  );
}

export default function CargoClaimsOverview() {
  return (
    <div className="list-zero-workflow">
      <section className="workflow-card">
        <h2>Cargo Claims</h2>
        <p>
          A cargo claim covers goods lost or damaged in transit. AZADEA files the
          claim and supplies the paperwork; Clover assesses it and settles it with
          a credit note. Both sides work from the same claim record, so neither
          has to ask the other where something has got to.
        </p>
        <p>
          Every change of status is recorded against the claim together with who
          made it, which is what the history on a claim shows you.
        </p>
      </section>

      <section className="workflow-card">
        <h2>How a claim moves</h2>
        <LifecycleDiagram />
      </section>

      <section className="workflow-card">
        <h2>The five stages</h2>
        <ol className="workflow-steps">
          <li>
            <strong>Notification.</strong> AZADEA files the claim with the company
            code and shipment number. It comes back with a reference such as
            CLM-2026-000123 and the status Received. The 90 day clock for
            documents starts here.
          </li>
          <li>
            <strong>Completing the file.</strong> AZADEA sends the details, the
            per business unit lines, and the six mandatory documents. They can
            arrive in any order. When the details and all six documents are in,
            the claim becomes Documented on its own.
          </li>
          <li>
            <strong>Review.</strong> Clover picks the claim up, which moves it to
            In review, and works through it.
          </li>
          <li>
            <strong>Decision.</strong> Clover accepts, rejects or marks the claim
            unsettled, always with a reason. An unsettled claim is one still being
            discussed and can still be accepted or rejected later.
          </li>
          <li>
            <strong>Settlement.</strong> Issuing the credit note closes the claim.
            The note and the closure are recorded together, so a closed claim
            always shows the note that closed it.
          </li>
        </ol>
      </section>

      <section className="workflow-card">
        <h2>Two rules worth knowing</h2>

        <div className="workflow-callout">
          <strong>Filing the same claim twice is safe.</strong> A claim is
          identified by its company code and shipment number. If the same notification
          arrives again, because a job was re-run or a reply was lost, it returns
          the original reference instead of opening a second claim for the same
          shipment. Corrections after filing go through the details call, not by
          filing again.
        </div>

        <div className="workflow-callout workflow-callout--danger">
          <strong>A claim with no documents is cancelled after 90 days.</strong>{' '}
          A claim still sitting at Received when its document deadline passes is
          cancelled automatically overnight. Clover can move that deadline while
          the claim is still at Received, and uploading the last document at any
          point before it stops the cancellation.
        </div>
      </section>
    </div>
  );
}
